import type { Payout } from '../types/payout';
import { getCryptoPriceAtDate } from './coingecko';
import { roundAmount } from './numbers';

// Simple types valued from a single crypto's market price. 'transfert' carries no value.
const FILLABLE_SIMPLE_TYPES: Payout['type'][] = ['payout', 'stacking', 'achat', 'vente'];

const simpleSideNeedsFill = (payout: Payout): boolean =>
  FILLABLE_SIMPLE_TYPES.includes(payout.type) &&
  !!payout.crypto?.id &&
  !!payout.amount &&
  !payout.totalValue;

const swapSentNeedsFill = (payout: Payout): boolean =>
  payout.type === 'swap' && !!payout.sentCrypto?.id && !!payout.sentAmount && !payout.sentTotalValue;

const swapReceivedNeedsFill = (payout: Payout): boolean =>
  payout.type === 'swap' && !!payout.receivedCrypto?.id && !!payout.receivedAmount && !payout.receivedTotalValue;

export const needsValueFill = (payout: Payout): boolean =>
  simpleSideNeedsFill(payout) || swapSentNeedsFill(payout) || swapReceivedNeedsFill(payout);

const fillSimplePayout = async (payout: Payout): Promise<Payout | null> => {
  const price = await getCryptoPriceAtDate(payout.crypto!.id, new Date(payout.date));
  if (price === null) return null;
  return { ...payout, unitPrice: price, totalValue: roundAmount(payout.amount! * price) };
};

const fillSwapPayout = async (payout: Payout): Promise<Payout | null> => {
  let result = payout;
  let filledAnySide = false;

  if (swapSentNeedsFill(payout)) {
    const price = await getCryptoPriceAtDate(payout.sentCrypto!.id, new Date(payout.date));
    if (price !== null) {
      result = { ...result, sentUnitPrice: price, sentTotalValue: roundAmount(payout.sentAmount! * price) };
      filledAnySide = true;
    }
  }

  if (swapReceivedNeedsFill(payout)) {
    const price = await getCryptoPriceAtDate(payout.receivedCrypto!.id, new Date(payout.date));
    if (price !== null) {
      result = { ...result, receivedUnitPrice: price, receivedTotalValue: roundAmount(payout.receivedAmount! * price) };
      filledAnySide = true;
    }
  }

  return filledAnySide ? result : null;
};

/**
 * Fills totalValue/unitPrice (or sentTotalValue/receivedTotalValue for swaps) for payouts
 * missing it (e.g. imported from a CSV with blank value cells) by looking up the historical
 * market price. Runs sequentially: getCryptoPriceAtDate already queues requests through a
 * shared rate limiter, but a tight loop of many payouts would otherwise spam CoinGecko with
 * concurrent calls.
 */
export const fillMissingPayoutValues = async (
  payouts: Payout[]
): Promise<{ updated: Payout[]; failed: Payout[] }> => {
  const updated: Payout[] = [];
  const failed: Payout[] = [];

  for (const payout of payouts.filter(needsValueFill)) {
    const filled = payout.type === 'swap' ? await fillSwapPayout(payout) : await fillSimplePayout(payout);
    if (filled === null) {
      failed.push(payout);
    } else {
      updated.push(filled);
    }
  }

  return { updated, failed };
};
